/**
 * #8352 — THE REQUESTED-OUTPUT LEDGER, ON THE WIRE.
 *
 * Split out of `ask-helix.ts` (PR #8527 cure round 2): that file is pinned
 * debt under the repo size law and may only shrink, so the requested-output
 * DTO contract lives in this focused module and is re-exported there.
 */

/**
 * #8352 — THE PUBLIC OUTCOME REASON ON A LEDGER ENTRY, WIRE-SAFE.
 *
 * `kind` is the closed resolution-gap vocabulary
 * (`RuntimeV3ResolutionGapKind`), carried as a plain string at this shared
 * boundary rather than importing the runtime-v3 union (this module owns no
 * runtime-v3 dependency). Deliberately carries no `detail`/`message`: that
 * field on the private `RuntimeV3ResolutionGap` is a machine diagnostic
 * (a dataset id, a draft phrase) that must never reach a reader.
 */
export type AskHelixRequestedOutputOutcomeReasonDto = {
  kind: string;
  cause?: { kind: 'dependency'; blockedBy: string[] };
};

/**
 * #8352 — ONE ENTRY OF THE REQUESTED-OUTPUT LEDGER, ON THE WIRE.
 *
 * Mirrors `RuntimeV3RequestedOutputOutcome` (`@helix/lib/runtime-v3/answer-object`)
 * at the shared DTO boundary. `operation` and `dependsOn` are the request's
 * own typed facts, never invented. #8861: a grouped source output whose
 * provider resolved also carries `label`, the resolved family's catalog
 * display name (never draft text), and `populationKey`, an equality key over
 * its dataset revision, grouping and member filters (refs only).
 */
export type AskHelixRequestedOutputOutcomeDto = {
  outputId: string;
  requested: {
    operation: string;
    dependsOn: string[];
    label?: string;
    populationKey?: string;
    /** #8861 slice 3: the grouped output's dimensions, in plan order (refs only). */
    groupBy?: string[];
  };
  state: 'computed' | 'unresolved' | 'blocked_by_dependency' | 'not_executed';
  reason?: AskHelixRequestedOutputOutcomeReasonDto;
  claimIds: string[];
};
